import { useMsal } from '@azure/msal-react'
import { faImage, faPencil } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AspectRatio, Group, Image, Loader, Stack, Text } from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import imageCompression from 'browser-image-compression'
import { useState } from 'react'
import { uploadBlobFromFile } from '../../../utils/fileUtils'
import { convertHeicToJpeg } from '../../../utils/imageUtils'

interface DevicePhotosStepProps {
  imageUrl: string | null
  updateImageUrl: (url: string) => void
}

function DevicePhotosStep(props: DevicePhotosStepProps) {
  const { imageUrl, updateImageUrl } = props
  const [isUploading, setIsUploading] = useState(false)
  const { instance } = useMsal()

  return (
    <Stack gap={20} w='80%' maw={720}>
      <Dropzone
        onDrop={async (files: File[]) => {
          setIsUploading(true)
          try {
            // Compress image before HEIC conversion
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            }
            let compressedFile: File
            try {
              compressedFile = await imageCompression(files[0], options)
            } catch (compressionError) {
              console.log('Image compression failed. Uploading original file.')
              compressedFile = files[0]
            }
            // Convert HEIC to JPEG if needed
            const processedFile = await convertHeicToJpeg(compressedFile)
            const fileUrl = await uploadBlobFromFile(
              'devices',
              processedFile,
              instance,
            )
            updateImageUrl(fileUrl)
          } catch (error) {
            let errorMessage = 'The file failed to upload. Please try again.'
            if (error instanceof Error) {
              if (error.message.includes('Failed to convert HEIC')) {
                errorMessage =
                  'Failed to convert HEIC image. Please try a different image format.'
              } else if (error.message.includes('Failed to upload file')) {
                errorMessage =
                  'Network error. Please check your connection and try again.'
              } else {
                errorMessage = error.message
              }
            }
            notifications.show({
              title: 'Upload Failed',
              message: errorMessage,
              color: 'red',
              autoClose: 5000,
            })
          } finally {
            setIsUploading(false)
          }
        }}
        onReject={(files: any) => {
          const errors = files.map((file: any) => file.errors).flat()
          let errorMessage = 'The file was rejected. Please try again.'

          if (errors.length > 0) {
            const error = errors[0]
            if (error.code === 'file-too-large') {
              errorMessage =
                'File is too large. Please select a file smaller than 5MB.'
            } else if (error.code === 'file-invalid-type') {
              errorMessage =
                'File type not supported. Please select a valid image file.'
            } else if (error.code === 'too-many-files') {
              errorMessage = 'Too many files. Please select only one file.'
            }
          }

          notifications.show({
            title: 'File Rejected',
            message: errorMessage,
            color: 'red',
            autoClose: 5000,
          })
        }}
        maxSize={5 * 1024 ** 2}
        accept={[
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/webp',
          'image/heic',
          'image/heif',
        ]}
      >
        <Group
          justify='center'
          gap='xl'
          mih={400}
          style={{ pointerEvents: 'none' }}
        >
          {isUploading ? (
            <Loader />
          ) : imageUrl ? (
            <Stack align='center'>
              <AspectRatio ratio={3 / 2}>
                <Image src={imageUrl} />
              </AspectRatio>
              <FontAwesomeIcon
                icon={faPencil}
                style={{ alignSelf: 'end', paddingRight: 10 }}
              />
            </Stack>
          ) : (
            <Group justify='center'>
              <FontAwesomeIcon icon={faImage} size='xl' />
              <Text size='xl' inline>
                Click here to add photo
              </Text>
            </Group>
          )}
        </Group>
      </Dropzone>
    </Stack>
  )
}

export default DevicePhotosStep
